"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Minus, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { QuantitySelectorProps } from "@/lib/models/quantitySelector";

export function QuantitySelector({
  quantity,
  min = 1,
  max,
  onChange,
  disabled = false,
  className,
  size = "sm",
}: QuantitySelectorProps) {
  const [localValue, setLocalValue] = useState<string>(String(quantity));

  useEffect(() => {
    setLocalValue(String(quantity));
  }, [quantity]);

  const handleDecrement = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled || quantity <= min) return;
      const nextValue = Math.max(min, quantity - 1);
      onChange(nextValue);
    },
    [disabled, min, onChange, quantity],
  );

  const handleIncrement = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      if (max !== undefined && quantity >= max) return;
      const nextValue =
        max !== undefined ? Math.min(max, quantity + 1) : quantity + 1;
      onChange(nextValue);
    },
    [disabled, max, onChange, quantity],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const sanitized = e.target.value.replace(/\D/g, "");
      setLocalValue(sanitized);

      if (sanitized === "") return;

      let parsed = parseInt(sanitized, 10);
      if (isNaN(parsed) || parsed < min) {
        parsed = min;
      } else if (max !== undefined && parsed > max) {
        parsed = max;
      }
      onChange(parsed);
    },
    [max, min, onChange],
  );

  const handleBlur = useCallback(() => {
    if (localValue === "" || parseInt(localValue, 10) < min) {
      setLocalValue(String(min));
      onChange(min);
      return;
    }
    const parsed = parseInt(localValue, 10);
    if (max !== undefined && parsed > max) {
      setLocalValue(String(max));
      onChange(max);
    }
  }, [localValue, max, min, onChange]);

  const isMin = quantity <= min;
  const isMax = max !== undefined && quantity >= max;
  const isSmall = size === "sm";

  return (
    <div
      data-no-navigate="true"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      className={cn(
        "no-navigate inline-flex items-center justify-between select-none transition-all duration-200",
        "rounded-md border border-slate-200 bg-slate-50/80 hover:bg-slate-50",
        "focus-within:border-primary/40 focus-within:ring-1 focus-within:ring-primary/20 focus-within:bg-white",
        isSmall ? "h-8 px-1" : "h-9 px-1.5",
        disabled &&
          "opacity-40 cursor-not-allowed pointer-events-none bg-slate-100",
        className,
      )}
    >
      <button
        type="button"
        aria-label="Disminuir cantidad"
        disabled={disabled || isMin}
        onClick={handleDecrement}
        className={cn(
          "inline-flex items-center justify-center rounded text-slate-500 transition-colors shrink-0",
          "hover:bg-white hover:text-primary active:scale-90",
          "disabled:opacity-30 disabled:pointer-events-none disabled:hover:bg-transparent",
          isSmall ? "h-7 w-7 sm:w-8" : "h-8 w-8 sm:w-9",
        )}
      >
        <Minus size={isSmall ? 13 : 15} strokeWidth={2.2} />
      </button>

      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        aria-label="Cantidad"
        disabled={disabled}
        value={localValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.currentTarget.blur();
          }
        }}
        className={cn(
          "w-full flex-1 text-center font-bold text-slate-800 bg-transparent border-0 outline-none p-0",
          "focus:text-primary tabular-nums",
          isSmall ? "text-xs sm:text-sm" : "text-sm sm:text-base",
        )}
      />

      <button
        type="button"
        aria-label="Aumentar cantidad"
        disabled={disabled || isMax}
        onClick={handleIncrement}
        className={cn(
          "inline-flex items-center justify-center rounded text-slate-500 transition-colors shrink-0",
          "hover:bg-white hover:text-primary active:scale-90",
          "disabled:opacity-30 disabled:pointer-events-none disabled:hover:bg-transparent",
          isSmall ? "h-7 w-7 sm:w-8" : "h-8 w-8 sm:w-9",
        )}
      >
        <Plus size={isSmall ? 13 : 15} strokeWidth={2.2} />
      </button>
    </div>
  );
}
